import { useEffect } from "react";
import FormFields from "../components/common/FormFields";
import { FormSection } from "../components/common/FormSection";
import useGet from "../hooks/common/useGet";
import { IFormSection, TError } from "../types/common";
import { API_CONFIGURABLES } from "./apis";
import { getResultOptions } from "./helpers";

export type ValidationRule = {
  value?: any;
  message: string;
};

export type ValidationRules = {
  required?: ValidationRule;
  minLength?: ValidationRule;
  maxLength?: ValidationRule;
  min?: ValidationRule;
  max?: ValidationRule;
  pattern?: ValidationRule;
  email?: ValidationRule;
  url?: ValidationRule;
  phone?: ValidationRule;
  alphaOnly?: ValidationRule;
  alphaNumeric?: ValidationRule;
  numeric?: ValidationRule;
  noSpaces?: ValidationRule;
  noSpecialChars?: ValidationRule;
  strongPassword?: ValidationRule;
  matchField?: ValidationRule & { field: string };
  custom?: (value: any) => boolean | string;
};

export const VALIDATION_PATTERNS = {
  // Only letters (A-Z, a-z)
  ALPHA_ONLY: /^[A-Za-z]+$/,

  // Letters with spaces
  ALPHA_WITH_SPACES: /^[A-Za-z\s]+$/,

  // Letters and numbers
  ALPHA_NUMERIC: /^[A-Za-z0-9]+$/,

  // Letters, numbers, and spaces
  ALPHA_NUMERIC_SPACES: /^[A-Za-z0-9\s]+$/,

  // Only numbers
  NUMERIC: /^[0-9]+$/,

  EMAIL:
    /^[a-zA-Z0-9]+([._-][a-zA-Z0-9]+)*@[a-zA-Z0-9]+([.-][a-zA-Z0-9]+)*\.[a-zA-Z]{2,}$/,

  // URL
  URL: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,

  // Phone (international format)
  PHONE: /^\+?[1-9]\d{1,14}$/,

  // Ugandan phone numbers
  PHONE_UG_MOBILE: /^(?:\+?256|0)7[0-9]{8}$/,
  PHONE_UG_LANDLINE: /^(?:\+?256|0)[34][0-9]{8}$/,
  PHONE_UG_PREMIUM: /^(?:\+?256|0)90[1-3][0-9]{6}$/,
  PHONE_UG_TOLL_FREE: /^0800[0-9]{6}$/,
  PHONE_UG_SHORTCODE: /^(116|999|112)$/,

  PHONE_UG:
    /^(?:(?:\+?256|0)(?:7[0-9]{8}|[34][0-9]{8}|90[1-3][0-9]{6})|0800[0-9]{6}|(?:116|999|112))$/,

  // Ugandan National Identification Number (NIN)
  // Format: 14 characters - letters and numbers (e.g., CM97006105MWFC)
  NIN: /^[A-Z0-9]{14}$/,

  // Tax Identification Number (TIN)
  // Format: 10 digits (e.g., 1016208677)
  TIN: /^[0-9]{10}$/,

  // Business Registration Number (BRN)
  // Format: 14 digits (e.g., 80020000888473)
  BRN: /^[0-9]{14}$/,

  // No spaces
  NO_SPACES: /^\S+$/,

  // No special characters (only letters, numbers, spaces)
  NO_SPECIAL_CHARS: /^[A-Za-z0-9\s]+$/,

  // Strong password (min 8 chars, 1 uppercase, 1 lowercase, 1 number, 1 special char)
  STRONG_PASSWORD:
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,

  // Postal code (various formats)
  POSTAL_CODE: /^[A-Z0-9]{3,10}$/i,

  // Credit card
  CREDIT_CARD: /^[0-9]{13,19}$/,

  // Date (YYYY-MM-DD)
  DATE_ISO: /^\d{4}-\d{2}-\d{2}$/,

  // Time (HH:MM)
  TIME: /^([01]\d|2[0-3]):([0-5]\d)$/,

  // Hex color
  HEX_COLOR: /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/,

  // IPv4
  IPV4: /^(\d{1,3}\.){3}\d{1,3}$/,

  // Username (letters, numbers, underscore, hyphen)
  USERNAME: /^[a-zA-Z0-9_-]{3,16}$/,

  // Slug (URL-friendly)
  SLUG: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
};

/**
 * Common validation rule presets
 */
export const VALIDATION_PRESETS = {
  // Name validations
  FIRST_NAME: {
    pattern: {
      value: VALIDATION_PATTERNS.ALPHA_ONLY,
      message: "First name must contain only letters",
    },
    minLength: {
      value: 3,
      message: "First name must be at least 3 characters",
    },
    maxLength: {
      value: 50,
      message: "First name must not exceed 50 characters",
    },
  },

  LAST_NAME: {
    pattern: {
      value: VALIDATION_PATTERNS.ALPHA_ONLY,
      message: "Last name must contain only letters",
    },
    minLength: {
      value: 3,
      message: "Last name must be at least 3 characters",
    },
    maxLength: {
      value: 50,
      message: "Last name must not exceed 50 characters",
    },
  },

  FULL_NAME: {
    pattern: {
      value: VALIDATION_PATTERNS.ALPHA_WITH_SPACES,
      message: "Name must contain only letters and spaces",
    },
    minLength: {
      value: 3,
      message: "Name must be at least 3 characters",
    },
    maxLength: {
      value: 100,
      message: "Name must not exceed 100 characters",
    },
  },

  // Email validation
  EMAIL: {
    pattern: {
      value: VALIDATION_PATTERNS.EMAIL,
      message: "Please enter a valid email address",
    },
    custom: (value: string) => {
      if (!value) return true;

      // Split email into prefix and domain
      const parts = value.split("@");
      if (parts.length !== 2) return "Invalid email format";

      const [prefix, domainPart] = parts;
      const domain = domainPart.split(".")[0]; // Get domain without TLD

      // Check if prefix contains at least one letter
      if (!/[a-zA-Z]/.test(prefix)) {
        return "Email prefix must contain at least one letter";
      }

      // Check if domain contains at least one letter
      if (!/[a-zA-Z]/.test(domain)) {
        return "Email domain must contain at least one letter";
      }

      return true;
    },
  },

  // Phone validation
  PHONE: {
    pattern: {
      value: VALIDATION_PATTERNS.PHONE,
      message: "Please enter a valid phone number",
    },
    minLength: {
      value: 10,
      message: "Phone number must be at least 10 digits",
    },
  },

  // Ugandan phone number validations
  PHONE_UG: {
    pattern: {
      value: VALIDATION_PATTERNS.PHONE_UG,
      message:
        "Please enter a valid Ugandan phone number (e.g., +256700000000 or 0700000000)",
    },
  },

  PHONE_UG_MOBILE: {
    pattern: {
      value: VALIDATION_PATTERNS.PHONE_UG_MOBILE,
      message:
        "Please enter a valid Ugandan mobile number (e.g., +256700000000 or 0700000000)",
    },
  },

  PHONE_UG_LANDLINE: {
    pattern: {
      value: VALIDATION_PATTERNS.PHONE_UG_LANDLINE,
      message:
        "Please enter a valid Ugandan landline number (e.g., +256312000000 or 0312000000)",
    },
  },

  // Ugandan ID validations
  NIN: {
    pattern: {
      value: VALIDATION_PATTERNS.NIN,
      message: "NIN must be 14 characters (e.g., CM9XXXXXXXXXXXFC)",
    },
    minLength: {
      value: 14,
      message: "NIN must be exactly 14 characters",
    },
    maxLength: {
      value: 14,
      message: "NIN must be exactly 14 characters",
    },
  },

  TIN: {
    pattern: {
      value: VALIDATION_PATTERNS.TIN,
      message: "TIN must be 10 digits (e.g., 1016XXXXXXXXXX)",
    },
    minLength: {
      value: 10,
      message: "TIN must be exactly 10 digits",
    },
    maxLength: {
      value: 10,
      message: "TIN must be exactly 10 digits",
    },
  },

  BRN: {
    pattern: {
      value: VALIDATION_PATTERNS.BRN,
      message: "BRN must be 14 digits (e.g., 8002XXXXXXXXXX)",
    },
    minLength: {
      value: 14,
      message: "BRN must be exactly 14 digits",
    },
    maxLength: {
      value: 14,
      message: "BRN must be exactly 14 digits",
    },
  },

  // Password validations
  PASSWORD: {
    minLength: {
      value: 8,
      message: "Password must be at least 8 characters",
    },
  },

  STRONG_PASSWORD: {
    pattern: {
      value: VALIDATION_PATTERNS.STRONG_PASSWORD,
      message:
        "Password must contain at least 8 characters, 1 uppercase, 1 lowercase, 1 number, and 1 special character",
    },
    minLength: {
      value: 8,
      message: "Password must be at least 8 characters",
    },
  },

  // Username validation
  USERNAME: {
    pattern: {
      value: VALIDATION_PATTERNS.USERNAME,
      message:
        "Username must be 3-16 characters and contain only letters, numbers, underscore, or hyphen",
    },
    minLength: {
      value: 3,
      message: "Username must be at least 3 characters",
    },
    maxLength: {
      value: 16,
      message: "Username must not exceed 16 characters",
    },
  },

  // Numeric validations
  POSITIVE_NUMBER: {
    min: {
      value: 0,
      message: "Value must be a positive number",
    },
  },

  POSITIVE_INTEGER: {
    pattern: {
      value: VALIDATION_PATTERNS.NUMERIC,
      message: "Value must be a positive integer",
    },
    min: {
      value: 0,
      message: "Value must be a positive number",
    },
  },

  // Amount/Currency validation
  AMOUNT: {
    min: {
      value: 0.01,
      message: "Amount must be greater than 0",
    },
  },

  // URL validation
  URL: {
    pattern: {
      value: VALIDATION_PATTERNS.URL,
      message: "Please enter a valid URL",
    },
  },

  // Business name
  BUSINESS_NAME: {
    pattern: {
      value: VALIDATION_PATTERNS.ALPHA_NUMERIC_SPACES,
      message: "Business name can only contain letters, numbers, and spaces",
    },
    minLength: {
      value: 3,
      message: "Business name must be at least 3 characters",
    },
    maxLength: {
      value: 100,
      message: "Business name must not exceed 100 characters",
    },
  },

  // Address
  ADDRESS: {
    minLength: {
      value: 5,
      message: "Address must be at least 5 characters",
    },
    maxLength: {
      value: 200,
      message: "Address must not exceed 200 characters",
    },
  },

  // Postal/Zip code
  POSTAL_CODE: {
    pattern: {
      value: VALIDATION_PATTERNS.POSTAL_CODE,
      message: "Please enter a valid postal code",
    },
  },

  // Description/Notes
  DESCRIPTION: {
    minLength: {
      value: 10,
      message: "Description must be at least 10 characters",
    },
    maxLength: {
      value: 500,
      message: "Description must not exceed 500 characters",
    },
  },

  SHORT_TEXT: {
    maxLength: {
      value: 100,
      message: "Text must not exceed 100 characters",
    },
  },

  MEDIUM_TEXT: {
    maxLength: {
      value: 255,
      message: "Text must not exceed 255 characters",
    },
  },

  LONG_TEXT: {
    maxLength: {
      value: 1000,
      message: "Text must not exceed 1000 characters",
    },
  },
};

export const combineValidations = (
  ...validations: Partial<ValidationRules>[]
): ValidationRules => {
  return validations.reduce((acc, curr) => ({ ...acc, ...curr }), {});
};

export const createCustomValidation = (
  validator: (value: any) => boolean,
  message: string,
): ValidationRule => {
  return {
    value: validator,
    message,
  };
};

export function createForm(
  sections: IFormSection[],
  form: any,
  updateFormValues: any,
  updateFormValue: any,
  onChange?: any,
  error?: any,
  options?: any,
  setError?: any,
): JSX.Element {
  const alphabet = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"];

  return (
    <>
      {sections?.map((section, i) => {
        const { title, fields, visible = true, component, ...props } = section;

        if (!visible) {
          // set all fields in the section to visible false
          try {
            fields.forEach((field) => (field.visible = false));
          } catch (e) {
            console.error(e);
          }
          return null;
        }
        return (
          <div key={i}>
            <FormSection
              section={`Section ${alphabet[i]}`}
              title={title}
              key={i}
              {...props}
            >
              {/* FORM FIELDS */}
              <FormFields
                fields={fields}
                error={error as TError}
                form={form}
                onChange={onChange}
                options={options}
                updateFormValue={updateFormValue}
                updateFormValues={updateFormValues}
                setError={setError}
              />
              {/* CUSTOM COMPONENT */}
              {component}
            </FormSection>
          </div>
        );
      })}
    </>
  );
}
interface IConfig {
  name: string;
  key: string;
  url?: string;
  itemName?: string;
}
export const useGetConfigurableOptions = (config: IConfig[]) => {
  const optionsState: any = {};
  const loaders: any = {};
  const dataRefs: any = {};

  config.forEach(({ key, name }) => {
    const { loadData, data } = useGet();
    loaders[key] = loadData;
    dataRefs[key] = data;
    optionsState[name] = [];
  });

  useEffect(() => {
    config.forEach(({ key, url }) => {
      loaders[key]({
        api: url || API_CONFIGURABLES,
        params: url ? {} : { key },
      });
    });
  }, []);

  config.forEach(({ key, name, itemName }) => {
    optionsState[name] = getResultOptions(dataRefs[key], itemName);
  });

  return optionsState;
};
